/**
 * Google OAuth Callback Handler - Liquid Glass UI
 * Handles redirect from Google OAuth
 */
import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';

const GoogleCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double processing
      if (hasProcessed.current) return;
      hasProcessed.current = true;

      const token = searchParams.get('token');
      const error = searchParams.get('error');

      if (error) {
        console.error('Google OAuth error:', error);
        navigate('/login?error=google_oauth_failed', { replace: true });
        return;
      }

      if (!token) {
        navigate('/login?error=invalid_callback', { replace: true });
        return;
      }

      try {
        // Fetch user data with the token
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001'}/api/auth/me`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch user data');
        }

        const userData = await response.json();

        // Store token and user in localStorage first
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));

        // Update auth context state
        login(userData, token);

        // Small delay to ensure state propagates before navigation
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 100);

      } catch (err) {
        console.error('OAuth callback error:', err);
        navigate('/login?error=callback_failed', { replace: true });
      }
    };

    handleCallback();
  }, [searchParams, navigate, login]);

  return (
    <div className="min-h-screen liquid-bg flex items-center justify-center">
      {/* Animated Background Blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="liquid-blob blob-1"></div>
        <div className="liquid-blob blob-2"></div>
        <div className="liquid-blob blob-3"></div>
      </div>

      <div className="relative z-10 glass-panel p-10 text-center">
        <div className="w-16 h-16 mx-auto mb-6">
          <div className="w-full h-full rounded-full border-4 border-teal-200 border-t-teal-600 animate-spin"></div>
        </div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Completing sign in...</h2>
        <p className="text-gray-500">Please wait while we set up your account</p>
      </div>
    </div>
  );
};

export default GoogleCallback;
